import { apiClient } from '../client';
import { dineEndpoints } from '../endpoints/dine.endpoints';
import { call } from './unwrap';
import { hydrateKot, hydrateKotPrint } from '../hydrate';
import type { KotDto, KotPrintDto, KotsQuery } from '../types/dine.types';

const one = (p: Promise<KotDto>) => p.then(hydrateKot);

/** Every return path is hydrated (src/api/hydrate.ts). */
export const kotService = {
  /** GET /kots?station=&status=live|all&mine= — READY first, then oldest sentAt */
  list(q: KotsQuery = {}): Promise<KotDto[]> {
    return call<KotDto[]>(apiClient.get(dineEndpoints.kots(q)), 'Could not load the kitchen').then((rows) =>
      rows.map(hydrateKot),
    );
  },

  /** GET /kots/{id} */
  get(id: string): Promise<KotDto> {
    return one(call<KotDto>(apiClient.get(dineEndpoints.kot(id))));
  },

  /** POST /kots/{id}/accept — NEW → ACCEPTED */
  accept(id: string): Promise<KotDto> {
    return one(call<KotDto>(apiClient.post(dineEndpoints.kotAccept(id))));
  },

  /** POST /kots/{id}/ready — → READY (all lines) */
  ready(id: string): Promise<KotDto> {
    return one(call<KotDto>(apiClient.post(dineEndpoints.kotReady(id))));
  },

  /** POST /kots/{id}/lines/{lineId}/ready — one line ahead of its ticket */
  lineReady(id: string, lineId: string): Promise<KotDto> {
    return one(call<KotDto>(apiClient.post(dineEndpoints.kotLineReady(id, lineId))));
  },

  /** POST /kots/{id}/serve — READY → SERVED */
  serve(id: string): Promise<KotDto> {
    return one(call<KotDto>(apiClient.post(dineEndpoints.kotServe(id)), 'Could not mark it served'));
  },

  /** POST /kots/{id}/reject — reason required */
  reject(id: string, reason: string): Promise<KotDto> {
    return one(call<KotDto>(apiClient.post(dineEndpoints.kotReject(id), { reason })));
  },

  /** GET /kots/{id}/print */
  print(id: string): Promise<KotPrintDto> {
    return call<KotPrintDto>(apiClient.get(dineEndpoints.kotPrint(id))).then(hydrateKotPrint);
  },
};
